import { useEffect, useState } from "react"

const AGE_GATE_STORAGE_KEY = "ageVerified"
const AGE_GATE_STORAGE_VALUE = "true"
const AGE_GATE_HOST = "age-gate"
const AGE_GATE_OVERLAY = "#age-gate-modal"

export interface DeferredReadyOptions {
  waitForAgeGate?: boolean
  waitForStorageKey?: string
  waitForStorageValue?: string
  waitUntilHidden?: string
  waitUntilHiddenHost?: string
}

function storageSatisfied(key: string, value: string): boolean {
  if (typeof window === "undefined") return false
  return localStorage.getItem(key) === value
}

function findInHostShadow(hostSelector: string, overlaySelector: string): Element | null {
  try {
    const hosts = document.querySelectorAll(hostSelector)
    for (const host of hosts) {
      const found = host.shadowRoot?.querySelector(overlaySelector)
      if (found) return found
    }
  } catch {
    return null
  }
  return null
}

function findOverlay(overlaySelector: string, hostSelector?: string): Element | null {
  if (typeof document === "undefined" || !overlaySelector) return null
  try {
    const light = document.querySelector(overlaySelector)
    if (light) return light
  } catch {
    return null
  }
  if (hostSelector) {
    const shadowed = findInHostShadow(hostSelector, overlaySelector)
    if (shadowed) return shadowed
  }
  return findInHostShadow(AGE_GATE_HOST, overlaySelector)
}

function isDeferredReady(options: DeferredReadyOptions): boolean {
  const waitForAgeGate = Boolean(options.waitForAgeGate)
  const storageKey =
    options.waitForStorageKey?.trim() ||
    (waitForAgeGate ? AGE_GATE_STORAGE_KEY : "")
  const storageValue =
    options.waitForStorageValue?.trim() || AGE_GATE_STORAGE_VALUE
  const overlaySelector =
    options.waitUntilHidden?.trim() ||
    (waitForAgeGate ? AGE_GATE_OVERLAY : "")
  const hostSelector =
    options.waitUntilHiddenHost?.trim() || AGE_GATE_HOST

  if (!storageKey && !overlaySelector) return true

  if (storageKey && !storageSatisfied(storageKey, storageValue)) return false
  if (overlaySelector && findOverlay(overlaySelector, hostSelector)) return false
  return true
}

export function useDeferredReady(options: DeferredReadyOptions): boolean {
  const waitForAgeGate = Boolean(options.waitForAgeGate)
  const storageKey = options.waitForStorageKey?.trim() || ""
  const storageValue = options.waitForStorageValue?.trim() || ""
  const waitUntilHidden = options.waitUntilHidden?.trim() || ""
  const waitUntilHiddenHost = options.waitUntilHiddenHost?.trim() || ""

  const [ready, setReady] = useState(() => isDeferredReady(options))

  useEffect(() => {
    const current = {
      waitForAgeGate,
      waitForStorageKey: storageKey,
      waitForStorageValue: storageValue,
      waitUntilHidden,
      waitUntilHiddenHost,
    }
    if (!waitForAgeGate && !storageKey && !waitUntilHidden) {
      setReady(true)
      return
    }

    const check = () => setReady(isDeferredReady(current))
    check()

    const shadowObservers: MutationObserver[] = []
    const observedShadows = new WeakSet<ShadowRoot>()
    const watchShadows = () => {
      const hostSel = waitUntilHiddenHost || AGE_GATE_HOST
      try {
        document.querySelectorAll(hostSel).forEach((host) => {
          const root = host.shadowRoot
          if (!root || observedShadows.has(root)) return
          observedShadows.add(root)
          const shadowObserver = new MutationObserver(check)
          shadowObserver.observe(root, {
            childList: true,
            subtree: true,
          })
          shadowObservers.push(shadowObserver)
        })
      } catch {
        /* invalid selector */
      }
    }
    watchShadows()

    const observer = new MutationObserver(() => {
      watchShadows()
      check()
    })
    observer.observe(document.documentElement, {
      childList: true,
      subtree: true,
    })

    const interval = window.setInterval(check, 250)

    return () => {
      observer.disconnect()
      shadowObservers.forEach((item) => item.disconnect())
      window.clearInterval(interval)
    }
  }, [waitForAgeGate, storageKey, storageValue, waitUntilHidden, waitUntilHiddenHost])

  return ready
}
